import customFetch, { fetchDelete, fetchPost, fetchPut } from '@/lib/customFetch';
import { IErrorData } from '@/lib/errorHandler.ts';
import useLocalStorage from '@/lib/user-local-storage';
import { IResponse } from '@/model/IResponse';
import { QueryClient, QueryClientProvider, useMutation, UseMutationResult, useQuery, useQueryClient } from '@tanstack/react-query';

const queryClient = new QueryClient();

/**
 * Use in the root
 */
export function QueryContext({ children }: {children: JSX.Element}) {
    return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}

/**
 * Use in the component
 * @param url path, it will be appended to `BASE_URL`
 * @param queryKey set an **unique** name for request, it manages query `caching` for you based on query `keys`
 * @param queryParams aditional query string params
 * @param isEnabled Set this to `false` to disable automatic refetching when the query mounts or changes query keys. To refetch the query, use the `refetch method` returned. Defaults to `true`.
 * @param staleTime The time in milliseconds after data is considered stale. If set to `Infinity`, the data will never be considered stale.
 **/
export interface IQueryDataProps {
    url: string;
    queryKey: unknown[];
    queryParams?: QueryStringParams;
    refetchOnMount?: boolean | 'always';
    isEnabled?: boolean;
    staleTime?: number;
    sendCredentials?: boolean;
    retry?: number;
    refetchIntervalFn?: () => number;
    successFn?: () => any | void;

}

/**
 * @param page it will be appended as query string `required param`
 * @param pageSize it will be appended as query string `required param`
 **/
export interface IQueryDataPagedProps extends IQueryDataProps {
    page: number;
    pageSize: number;
}

export function QueryData<TResponse extends {}>(
        {
            url,
            queryKey,
            queryParams,
            refetchOnMount = false,
            isEnabled,
            staleTime,
            retry = 1,
            refetchIntervalFn,
        }: IQueryDataProps,
) {
    const [filters] = useLocalStorage('filters');
    const queryKeyWithFilters = [...queryKey, filters];
    return useQuery({
        queryKey: queryKeyWithFilters,
        queryFn : async () => {
            return await customFetch<IResponse<TResponse>>(
                    apendQueryString(url, queryParams),
                    {},
            );
        },
        enabled : isEnabled,
        staleTime : staleTime ?? Infinity,
        refetchOnMount : refetchOnMount,
        retry : retry,
        refetchInterval : refetchIntervalFn,
    });
}

export function QueryDataPaged<TResponse extends {}>(
        {
            url,
            queryKey,
            page,
            pageSize = 10,
            queryParams,
            refetchOnMount = false,
            isEnabled,
            staleTime,
            retry = 1,
            refetchIntervalFn,
        }: IQueryDataPagedProps,
) {
    const URI = apendQueryString(url, {
        Page : page,
        PageSize : pageSize,
        ...queryParams,
    });
    return useQuery({
        queryKey : [...queryKey, page, pageSize],
        queryFn : async () => {
            const response = await customFetch<TResponse>(URI, {});
            return response as TResponse;
        },
        keepPreviousData : true,
        staleTime : staleTime ?? 5000,
        enabled : isEnabled,
        refetchOnMount : refetchOnMount,
        retry : retry,
        refetchInterval : refetchIntervalFn,
    });
}

export type MutationFnProps<TRequest> = {data?: TRequest; queryString?: QueryStringParams};

export function UpdateData<TRequest extends {} = {}, TResponse extends {} = {}>(
        {
            url,
            queryKey,
        }: IQueryDataProps,
) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn : async ({ data, queryString }: MutationFnProps<TRequest>) => {
            return await fetchPut<TResponse, TRequest>(
                    apendQueryString(url, queryString),
                    data,
            );
        },
        onSuccess : () => {
            queryClient.invalidateQueries({ queryKey : queryKey });
        },
    });
}

export function AddData<TRequest extends object = {}, TResponse = {}>(
        { url, queryKey }: IQueryDataProps,
): UseMutationResult<TResponse, unknown, MutationFnProps<TRequest>> {
    const queryClient = useQueryClient();

    return useMutation<TResponse, IErrorData, MutationFnProps<TRequest>>({
        mutationFn : async ({ data, queryString }) => {
            const fullUrl = apendQueryString(url, queryString);
            return await fetchPost<any, TRequest>(fullUrl, data);
        },
        onSuccess : () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });
}

export function DeleteData<TRequest extends {} = {}, TResponse extends {} = {}>(
        {
            url,
            queryKey,
            successFn,
        }: IQueryDataProps,
) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn : async ({ data, queryString }: MutationFnProps<TRequest>) => {
            return await fetchDelete<TResponse, TRequest>(
                    apendQueryString(url, queryString),
                    data,
            );
        },
        onSuccess : () => {
            queryClient.invalidateQueries({ queryKey: queryKey });

            if (typeof successFn === 'function') {
                successFn();
            }
        },
    });
}

export function InvalidadeCachedValues(queryKey: unknown[]) {
    const queryClient = useQueryClient();
    queryClient.invalidateQueries({ queryKey : queryKey });
}

export type QueryStringParams = {[key: string]: string | number | number[] | boolean | Date};
const apendQueryString = (
        uri: string,
        params?: {[key: string]: string | number | number[] | boolean | Date},
) => {
    if (!params || Object.keys(params).length == 0) return uri;

    const queryString: string[] = [];
    for (const key in params) {
        if (Array.isArray(params[key])) {
            const elements: any = params[key];
            elements.forEach((element) => {
                queryString.push(`${key}=${element}`);
            });
        } else {
            queryString.push(`${key}=${params[key]}`);
        }
    }
    if (!uri.includes('?')) {
        uri = `${uri}?${queryString.join('&')}`;
    } else {
        uri = `${uri}&${queryString.join('&')}`;
    }
    return uri;
};
